import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import GradientText from "../components/GradientText";
import Seo from "../components/Seo";
import BackgroundVideo from "../components/BackgroundVideo";

interface Review {
  text: string;
  name: string;
}

const neonColors = ["#fb923c", "#f97316", "#ea580c", "#f59e0b", "#fb923c"];
const foodShowcaseSlides = [
  {
    image: "/images/pitbeefsandwich.avif",
    title: "Pit Beef Sandwiches",
  },
  {
    image: "/images/ribs.avif",
    title: "Slow-Smoked Ribs",
  },
  {
    image: "/images/halfchicken.avif",
    title: "BBQ Chicken",
  },
  {
    image: "/images/oceanfries.avif",
    title: "House Sides",
  },
];

const foodTrucks = [
  {
    name: "Bull on the Loose",
    image: "/images/TruckImage.webp",
    logo: "/images/BOTLLogo.webp",
    href: "https://www.bullontheloose.com",
    bookingHref: "https://www.bullontheloose.com/contact",
    description:
      "Pit beef, barbecue, and event-ready food truck service for neighborhoods, private parties, corporate lunches, and local gatherings.",
  },
  {
    name: "Retro Grill",
    image: "/images/RetroTruck.webp",
    logo: "/images/RetroGrillLogo.webp",
    href: "https://retrogrillec.com",
    bookingHref: "https://retrogrillec.com/contact",
    description:
      "Serving smash burgers, crab cakes, wraps, salads, and more for parties, events, and community stops around the area.",
  },
];

export default function HomePage() {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [currentFoodSlide, setCurrentFoodSlide] = useState(0);

  // Fetch reviews
  useEffect(() => {
    fetch("/reviews.json")
      .then((res) => res.json())
      .then((data) => setReviews(data.reviews))
      .catch((err) => console.error("Error loading reviews:", err));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFoodSlide((prev) => (prev + 1) % foodShowcaseSlides.length);
    }, 4500);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );

    document
      .querySelectorAll(
        ".animate-fade-in-left, .animate-fade-in-right, .animate-fade-in-up, .feature-card, .truck-card",
      )
      .forEach((el) => {
        observerRef.current?.observe(el);
      });

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const localBusinessSchema = useMemo(() => {
    const siteUrl = window.location.origin;

    return {
      "@context": "https://schema.org",
      "@type": "Restaurant",
      name: "The Canopy",
      url: siteUrl,
      email: "",
      telephone: "+1-443-288-1400",
      image: [`${siteUrl}/images/TheCanopyLogo.webp`],
      sameAs: [
        "https://www.facebook.com/TheCanopyEllicottcity",
        "https://www.instagram.com/thecanopyec",
      ],
      servesCuisine: ["Barbecue", "American"],
      areaServed: ["Howard County, MD", "Columbia, MD", "Ellicott City, MD"],
      priceRange: "$$",
    };
  }, []);

  return (
    <>
      <Seo
        title="Pit Beef & BBQ in Ellicott City, MD"
        description="The Canopy serves pit beef, BBQ, ribs, and more in Ellicott City, Maryland. View our menu, catering options, and contact details."
        path="/"
        structuredData={localBusinessSchema}
      />
      {/* Hero Section */}
      <section className="min-h-screen relative pt-[70px] overflow-hidden">
        <BackgroundVideo
          wrapperClassName="absolute inset-0"
          overlayClassName="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/35"
        />

        {/* Content - Left aligned */}
        <div className="relative z-10 min-h-[calc(100vh-70px)] flex flex-col justify-center px-6 md:px-12 lg:px-20 max-w-[700px]">
          <span className="text-accent-blue text-sm font-semibold tracking-[0.2em] uppercase mb-4">
            The Canopy
          </span>
          <h1 className="text-[clamp(3rem,10vw,6rem)] leading-[0.95] font-display font-bold text-white mb-6">
            <GradientText colors={neonColors} animationSpeed={6}>
              Baltimore's Best Pit Beef & BBQ!
            </GradientText>
          </h1>
          <p className="text-white/80 text-lg md:text-xl max-w-[500px] mb-10 leading-relaxed">
            Classic pit beef, slow-cooked barbecue, and house-made sides served
            hot, fresh, and made to order every day.
          </p>

          {/* Buttons */}
          <div className="flex gap-4 flex-wrap">
            <Link to="/menu" className="btn btn-primary btn-large">
              <span></span> View Menu
            </Link>
            <Link
              to="/catering"
              className="btn btn-large bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 hover:bg-white hover:text-bg-primary"
            >
              Catering
            </Link>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-[120px] relative overflow-hidden">
        <div className="absolute inset-0 -z-10 intro-section-bg intro-pattern-bg"></div>
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center relative z-10">
            {/* Intro Content */}
            <div className="animate-fade-in-left" id="intro-content">
              <span className="section-label">About Us</span>
              <GradientText
                colors={neonColors}
                animationSpeed={6}
                className="section-title"
              >
                Pit Beef Tradition, Done Right
              </GradientText>
              <p className="text-lg text-text-secondary mb-5 leading-relaxed">
                For more than 40 years, The Canopy has proudly served quality
                pit beef sandwiches, smoky barbecue, and the kind of hospitality
                you can only get from real people who care about every plate.
                Around here, it is simple: real folks, real pit beef, and food
                made fresh every day.
              </p>

              {/* Features */}
              <div className="flex flex-col gap-6 mt-10">
                <div
                  className="animate-fade-in-left feature-card flex gap-5 items-start p-5 bg-bg-card rounded-xl border border-border-default transition-all duration-500 hover:border-accent-purple hover:translate-x-2"
                  style={{ transitionDelay: "0.1s" }}
                >
                  <div className="w-[60px] h-[60px] rounded-xl overflow-hidden bg-bg-elevated flex-shrink-0 border border-border-default">
                    <img
                      src="/images/pitbeefsandwich.avif"
                      alt="Pit beef sandwich"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-body font-semibold text-text-primary mb-1 text-lg">
                      Fresh & Fast
                    </h3>
                    <p className="text-text-secondary text-[0.95rem] mb-3">
                      Made to order with the freshest ingredients
                    </p>
                    <Link
                      to="/menu"
                      className="inline-flex items-center gap-1 text-sm font-medium text-accent-pink hover:text-accent-pink-light transition-colors"
                    >
                      View Menu
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  </div>
                </div>
                <div
                  className="animate-fade-in-left feature-card flex gap-5 items-start p-5 bg-bg-card rounded-xl border border-border-default transition-all duration-500 hover:border-accent-purple hover:translate-x-2"
                  style={{ transitionDelay: "0.2s" }}
                >
                  <div className="w-[60px] h-[60px] rounded-xl overflow-hidden bg-bg-elevated flex-shrink-0 border border-border-default">
                    <img
                      src="/images/TheCanopyLogo.webp"
                      alt="The Canopy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-body font-semibold text-text-primary mb-1 text-lg">
                      Pull Up a Chair
                    </h3>
                    <p className="text-text-secondary text-[0.95rem] mb-3">
                      Real people, real pit beef, and fresh BBQ every day
                    </p>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-1 text-sm font-medium text-accent-pink hover:text-accent-pink-light transition-colors"
                    >
                      Contact Us
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  </div>
                </div>
                <div
                  className="animate-fade-in-left feature-card flex gap-5 items-start p-5 bg-bg-card rounded-xl border border-border-default transition-all duration-500 hover:border-accent-purple hover:translate-x-2"
                  style={{ transitionDelay: "0.3s" }}
                >
                  <div className="w-[60px] h-[60px] rounded-xl overflow-hidden bg-bg-elevated flex-shrink-0 border border-border-default">
                    <img
                      src="/images/meatplatters.avif"
                      alt="Catering platter"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-body font-semibold text-text-primary mb-1 text-lg">
                      Catering & Private Parties
                    </h3>
                    <p className="text-text-secondary text-[0.95rem] mb-3">
                      Book us for your next party or event
                    </p>
                    <Link
                      to="/catering"
                      className="inline-flex items-center gap-1 text-sm font-medium text-accent-pink hover:text-accent-pink-light transition-colors"
                    >
                      Learn More
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Reviews Showcase */}
            <div className="animate-fade-in-right" id="intro-visual">
              <p className="text-sm font-semibold uppercase tracking-[0.15em] text-accent-pink mb-4">
                What People Are Saying:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {reviews.slice(0, 4).map((review, index) => (
                  <div
                    key={index}
                    className={`review-card relative bg-bg-card rounded-[20px] p-6 border border-border-default transition-all duration-400 overflow-hidden animate-float-${
                      index + 1
                    } group hover:-translate-y-2 hover:border-accent-pink hover:shadow-lg-custom hover:shadow-glow-pink`}
                  >
                    {/* Quote icon */}
                    <svg
                      className="w-8 h-8 text-accent-pink/30 mb-3"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                    <p className="text-text-secondary text-[0.95rem] leading-relaxed mb-4 italic">
                      "{review.text}"
                    </p>
                    <p className="font-display font-semibold text-accent-blue text-sm">
                      — {review.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Food Trucks Section */}
      <section className="py-[90px] bg-bg-primary border-y border-border-default">
        <div className="container">
          <div className="max-w-[860px] mx-auto text-center mb-12">
            <span className="section-label block">On The Road</span>
            <div className="flex justify-center mb-5">
              <GradientText
                colors={neonColors}
                animationSpeed={6}
                className="section-title text-center"
              >
                Meet Our Food Trucks
              </GradientText>
            </div>
            <p className="text-text-secondary text-lg leading-relaxed">
              The Canopy family also owns Bull on the Loose and Retro Grill.
              Both trucks offer catering for events, private parties, and
              community stops. Book them for your next event and follow them on
              social media to see where they are headed next!
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {foodTrucks.map((truck, index) => (
              <article
                key={truck.name}
                className="truck-card animate-fade-in-up bg-bg-card border border-border-default rounded-[20px] overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:border-accent-pink hover:shadow-lg-custom hover:shadow-glow-pink"
                style={{ transitionDelay: `${index * 0.12}s` }}
              >
                <div className="relative h-[260px] sm:h-[320px] bg-bg-elevated overflow-hidden">
                  <img
                    src={truck.image}
                    alt={`${truck.name} food truck`}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                  <div className="absolute left-5 bottom-5 right-5 flex items-end justify-between gap-4">
                    <img
                      src={truck.logo}
                      alt={`${truck.name} logo`}
                      className="max-h-16 max-w-[170px] object-contain drop-shadow-lg"
                    />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl font-semibold text-text-primary mb-3">
                    {truck.name}
                  </h3>
                  <p className="text-text-secondary leading-relaxed mb-6">
                    {truck.description}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={truck.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      Visit Website
                    </a>
                    <a
                      href={truck.bookingHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn bg-bg-elevated text-text-primary border border-border-default hover:bg-bg-tertiary"
                    >
                      Book an Event
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Food Showcase Section */}
      <section className="py-[90px] bg-bg-secondary border-t border-border-default">
        <div className="container">
          <span className="section-label block text-center">From The Pit</span>
          <div className="flex justify-center mb-5">
            <GradientText
              colors={neonColors}
              animationSpeed={6}
              className="section-title text-center"
            >
              Here's What You Can Get
            </GradientText>
          </div>
          <p className="section-subtitle mb-8">
            Pit beef, ribs, chicken, and classic sides made fresh every day.
          </p>

          <div className="max-w-[1000px] mx-auto relative rounded-2xl overflow-hidden border border-border-default bg-bg-card">
            <div
              className="relative h-[260px] sm:h-[320px] md:h-[420px]"
              aria-label="Food showcase"
            >
              {foodShowcaseSlides.map((slide, index) => (
                <div
                  key={slide.image}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    index === currentFoodSlide ? "opacity-100" : "opacity-0"
                  }`}
                >
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                <p className="text-white text-lg sm:text-xl font-display font-semibold">
                  {foodShowcaseSlides[currentFoodSlide].title}
                </p>
                <div className="flex gap-2">
                  {foodShowcaseSlides.map((slide, index) => (
                    <button
                      key={slide.title}
                      onClick={() => setCurrentFoodSlide(index)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        index === currentFoodSlide
                          ? "w-8 bg-accent-pink"
                          : "w-4 bg-white/40 hover:bg-white/70"
                      }`}
                      aria-label={`View ${slide.title}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
